import { toTitleCase } from '@iotv/core-services';
import { Shape, ShapeRegistry, StencilShape, StencilShapeRegistry, registerDefaultShapes, requestUtils } from '@maxgraph/core';
import config from '../../../../config';
import { defaultIcon, defaultTypeToIconMap } from './defaultTypeToIconMap';
/** for loading venture-specific overrides */
const appCode = config.app.appCode.substring(0, 4);
const debug = process.env.REACT_APP_DEBUG && false;
/** adds default shapes to the {@link ShapeRegistry | `ShapeRegistry`} */
registerDefaultShapes();
/**
 * Given a URL / path, load the XML file
 * - parse the `<shapes />` for each `<shape />`
 * - transform the name into what LSC/Draw.IO uses (dot-separated, lowercase, underscored)
 * - add the {@link StencilShape | `StencilShape`} instance to the {@link StencilShapeRegistry | `StencilShapeRegistry`}
 * - return a `Record` of all the {@link StencilShape | `StencilShape`'s} loaded from the URL / path
 */
const loadStencil = (stencil) => {
    const stencilMap = {};
    const response = requestUtils.load(stencil);
    const shapes = response.getDocumentElement();
    debug && console.log('loadStencil', stencil, !!shapes);
    if (!shapes)
        return stencilMap;
    let shape = shapes.firstElementChild;
    const base = shapes.getAttribute('name')?.toLowerCase() ?? 'mxgraph';
    debug && console.log('loadStencil', base);
    while (shape) {
        const name = shape.getAttribute('name');
        const normalised = name?.toLocaleLowerCase().replaceAll(' ', '_');
        const fqdn = `${base}.${normalised}`;
        debug && console.log('loadStencil', fqdn);
        const stencilShape = new StencilShape(shape);
        stencilShape.fill = '#FFFFFF';
        stencilShape.stroke = '#000000';
        stencilShape.opacity = 100;
        StencilShapeRegistry.add(fqdn, stencilShape);
        stencilMap[fqdn] = stencilShape;
        shape = shape.nextElementSibling;
    }
    return stencilMap;
};
/**
 * Transform a dot-separated, lowercase, underscored stencil name into a URL / path to load it from
 *
 * @example stencilToPath('mxGraph.pid.pumps.centrifugal_pump_1')
 */
const stencilToPath = (stencil) => {
    if (typeof stencil !== 'string' || stencil.length === 0)
        return null;
    const [initial, first, second, third] = stencil.split('.');
    if (!initial.toLowerCase().includes('mxgraph'))
        return null;
    let path = `/${initial.toLowerCase()}/${first.toLowerCase()}`;
    if (second && third)
        path += `/${second.toLowerCase()}`;
    path += '.xml';
    const name = toTitleCase((third ?? second).replaceAll('_', ' '));
    debug && console.log('stencilToPathName', 'computed path and name:', path, name);
    return path;
};
const getShape = (name) => {
    const shapeConstructor = ShapeRegistry.get(name);
    if (shapeConstructor)
        return new shapeConstructor();
    return new Shape();
};
/**
 * Fetch the requested {@link StencilShape | `StencilShape`} or {@link Shape | `Shape`} instance from either the registry, or load it into the registry and then return it
 */
const getOrLoad = (name) => {
    if (typeof name !== 'string' || name.length === 0)
        return getShape(defaultIcon);
    const stencil = StencilShapeRegistry.get(name);
    debug && console.log('getOrLoad', 'getting', name);
    if (stencil)
        return stencil;
    const shapeConstructor = ShapeRegistry.get(name);
    if (shapeConstructor)
        return new shapeConstructor();
    debug && console.log('getOrLoad', 'loading', name);
    const path = stencilToPath(name);
    if (!path)
        return getShape(defaultIcon);
    return loadStencil(path)[name] ?? getShape(defaultIcon);
};
/**
 * Resolve type-to-icon map to either LSC default, or venture-specific override
 */
const typeToIconMap = await import(/* webpackPreload: true  */ `../../../../venture/${appCode}/cosmetics/icons`)
    .then((module) => {
    debug && console.log('EntityNetwork Schematic icons loaded venture-specific type to icon map:', module.typeToIconMap);
    return module.typeToIconMap;
})
    .catch((_) => {
    debug && console.log('No venture-specific type to icon map, using default...');
    return defaultTypeToIconMap;
});
/**
 * Given an LSC type, use the {@link typeToIconMap | `typeToIconMap`} to resolve it to a stencil name, and then get or load the {@link StencilShape | `StencilShape`} or {@link Shape | `Shape`} instance from the {@link registry | `registry`}
 */
export const resolveIcon = (type, context) => {
    const { defaultWidth, defaultHeight, minimumWidth, minimumHeight, defaultIconFill, defaultIconStroke, defaultTextFill, defaultTextStroke } = context;
    const fallback = {
        icon: getShape(defaultIcon),
        rotation: 0,
        width: Math.max(defaultWidth, minimumWidth),
        height: Math.max(defaultHeight, minimumHeight),
        iconFill: defaultIconFill,
        iconStroke: defaultIconStroke,
        textFill: defaultTextFill,
        textStroke: defaultTextStroke,
    };
    debug && console.log('resolveIcon', 'resolving icon', type);
    const resolved = typeToIconMap[type] ?? defaultTypeToIconMap[type];
    if (!resolved)
        return fallback;
    debug && console.log('resolveIcon', 'getting or loading stencil', resolved);
    return {
        icon: getOrLoad(resolved.icon),
        rotation: resolved.rotation ?? 0,
        width: Math.max(resolved.width ?? defaultWidth, minimumWidth),
        height: Math.max(resolved.height ?? defaultHeight, minimumHeight),
        iconFill: resolved.iconFill ?? defaultIconFill,
        iconStroke: resolved.iconStroke ?? defaultIconStroke,
        textFill: resolved.textFill ?? defaultTextFill,
        textStroke: resolved.textStroke ?? defaultTextStroke,
    };
};
